<script setup lang="ts">
import InitialsAvatar from '@/components/general/InitialsAvatar.vue';
import useContactsService from '@/composables/apiServices/useContactsService';
import type {
  IGroupInitialsAvatar,
  IGroupInitialsAvatarEvents
} from '@/types/components/general/GroupInitialsAvatar';
import type { VIntersectCallback } from '@/types/utils/v-intersect';
import { computed, ref, watch } from 'vue';

const props = withDefaults(defineProps<IGroupInitialsAvatar>(), { size: '40' });

const emit = defineEmits<IGroupInitialsAvatarEvents>();

const isComponentVisible = ref(false);
const currentGroupId = ref(props.groupId ?? null);
const shouldFetchAvatar = computed(() => (props.hasAvatar ?? false) && isComponentVisible.value);

const { getGroupAvatar } = useContactsService();
const { data: avatarData } = getGroupAvatar(currentGroupId, shouldFetchAvatar);

const fetchedAvatarUrl = ref<string | null>(null);

const onIntersect: VIntersectCallback = (isIntersecting) => {
  isComponentVisible.value = isIntersecting;
};
watch(
  () => avatarData.value,
  (newValue) => {
    if (newValue?.avatar) {
      fetchedAvatarUrl.value = newValue.avatar;
      emit('fileLoaded', newValue.avatar);
    } else {
      fetchedAvatarUrl.value = null;
    }
  },
  { immediate: true }
);

const groupAvatar = computed(() => {
  if (props.image) {
    return props.image;
  } else if (fetchedAvatarUrl.value && props.hasAvatar) {
    return fetchedAvatarUrl.value;
  }
  return undefined;
});
</script>

<template>
  <InitialsAvatar
    v-intersect="onIntersect"
    :image="groupAvatar"
    :name="name"
    :size="size"
    :color="color"
  />
</template>
